import * as React from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Cookies from "js-cookie";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import { useSetting } from "@/context";
import { getOfficesAPI } from "@/util/api/API";
import { Office, ONLY_CONTRACT } from "@/models/office";
import {
  QuickChargerRemainingTime as QuickChargerRemainingTimeModel,
  SearchQuickChargerRemainingTime,
  SearchQuickChargerRemainingTimeValidator,
} from "@/models/quickChargerRemainingTime";
import { getQuickChargerRemainingTimeAPI } from "@/util/api/quickChargerRemainingTime";
import { QuickChargerRemainingTimeList } from "./QuickChargerRemainingTimeList";
import Title from "@/components/molecules/Title";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { QuickChargerRemainingTimeConstants } from "./QuickChargerRemainingTimeConstants";
import { SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";
import { Typography } from "@mui/material";
import { getShortDatetime } from "@/util/datetime";

function QuickChargerRemainingTimeContent() {
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = React.useState<Office>();
  const setting = useSetting();
  const [searchData, setSearchData] = React.useState<SearchQuickChargerRemainingTime>({});
  const [searchDataValidator, setSearchDataValidator] = React.useState<SearchQuickChargerRemainingTimeValidator>({});
  const [data, setData] = React.useState<QuickChargerRemainingTimeModel[]>([]);
  const [noSearchResult, setNoSearchResult] = React.useState<boolean>(false);
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);
  const [activeSearchData, setActiveSearchData] = React.useState<SearchQuickChargerRemainingTime | null>(null);
  const [updatedAt, setUpdatedAt] = React.useState<Date | null>(null);

  const getData = React.useCallback((search: SearchQuickChargerRemainingTime) => {
    getQuickChargerRemainingTimeAPI(search)
      .then(({ data }) => {
        setData(data || []);
        setNoSearchResult(!data || data.length === 0);
      })
      .catch((err) => {
        console.log("==Error when get QuickChargerRemainingTime list==", err);
        setData([]);
        setNoSearchResult(true);
      })
      .finally(() => {
        const now = new Date();
        setUpdatedAt(now);
      });
  }, []);

  const validateCheck = React.useCallback(() => {
    const validator: SearchQuickChargerRemainingTimeValidator = {};
    if (!officeSelected?.id) {
      validator.officeId = true;
    }
    return validator;
  }, [officeSelected]);

  const handleSearchData = React.useCallback(() => {
    const validator = validateCheck();
    if (Object.keys(validator).length) {
      return setSearchDataValidator({
        ...validator,
      });
    }

    setActiveSearchData(searchData);
    getData(searchData);
  }, [validateCheck, getData, searchData]);

  const onChangeOffice = React.useCallback(
    (office: Office | undefined) => {
      setSearchData({
        ...searchData,
        officeId: office?.id,
      });
      setSearchDataValidator({
        ...searchDataValidator,
        officeId: false,
      });
      setOfficeSelected(office);
      Cookies.set(SINGLE_OFFICE_ID, office?.id.toString() || "");
    },
    [searchData, searchDataValidator]
  );

  const onClickResetSearchData = () => {
    setOfficeSelected(undefined);
    setSearchData({});
    setSearchDataValidator({});
    setData([]);
    setNoSearchResult(false);
    setActiveSearchData(null);
  };

  // First load
  React.useEffect(() => {
    setting.updateTitleHeader(QuickChargerRemainingTimeConstants.Title);

    let ignore = false;
    const getOffices = async () => {
      try {
        const res = await getOfficesAPI(ONLY_CONTRACT);
        if (ignore) return;

        setOffices(res.data || []);
        const found = res.data.find(
          (d) => d.id === Number(Cookies.get(SINGLE_OFFICE_ID))
        );
        if (found) {
          setOfficeSelected(found);
          setSearchData((prev) => ({
            ...prev,
            officeId: found.id,
          }));
          setInitSearchFlag(true);
        }
      } catch (err) {
        console.log("==Error when get offices==", err);
      }
    };

    getOffices();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cookieの営業所IDが営業所リストに存在した場合
  React.useEffect(() => {
    if (initSearchFlag) {
      handleSearchData();
      setInitSearchFlag(false);
    }
  }, [initSearchFlag, handleSearchData]);

  // Auto refresh every 60 seconds
  React.useEffect(() => {
    if (!activeSearchData) return;
    const interval = setInterval(() => {
      getData(activeSearchData);
    }, 60000);
    return () => clearInterval(interval);
  }, [activeSearchData, getData]);

  return (
    <React.Fragment>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}>
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {QuickChargerRemainingTimeConstants.Title}
          </Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
              <Grid container spacing={2} alignItems="end">
                <Grid item xs={6} className="required">
                  <AutoCompleteSearch
                    options={offices}
                    autoHighlight
                    getOptionLabel={getOfficeOptionLabel}
                    value={officeSelected || undefined}
                    onChange={(e, value) => {
                      onChangeOffice(value || undefined);
                    }}
                    label={QuickChargerRemainingTimeConstants.OfficeName}
                    error={searchDataValidator.officeId}
                  />
                </Grid>
                <Grid item container justifyContent="flex-end" xs={6}>
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleSearchData}
                    sx={{ height: 40 }}
                  />
                  <CommonButton
                    sx={{ ml: 2, height: 40 }}
                    btnType="ResetSearchCriteria"
                    onClickBtn={onClickResetSearchData}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} md={12} lg={12} container justifyContent="flex-end">
            <Typography
              sx={{ fontSize: 16, pb: 0, color: "red", whiteSpace: "pre-wrap", textAlign: "right" }}
            >
              {updatedAt
                ? `更新日時: ${getShortDatetime(updatedAt?.toISOString())}`
                : ""}
            </Typography>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <QuickChargerRemainingTimeList data={data} updatedAt={updatedAt} />
              {noSearchResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export const QuickChargerRemainingTime = () => {
  return <QuickChargerRemainingTimeContent />;
};
